import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Modal, Row, Col, Spinner, Form as BSForm } from "react-bootstrap";
import styled from "styled-components";
import {
	CustomButton,
	CustomSelect,
	CustomInput,
	FormikFieldError,
} from "components/CustomForm";
import { MdClose } from "react-icons/md";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import {
	createUserGroup,
	updateUserGroup,
} from "network/apis/UserGroupsApi";
import { getPermissions } from 'network/apis/UserGroupsApi';
import { isBlank, getErrorMessage } from "helpers/strings";

const ModalHolder = styled(Modal)`
	font-family: 'YEKAN-BAKH-MEDIUM';
`;

const Header = styled.div`
	font-size: 18pt;
	width: 100%;
	text-align: center;
	margin-top: 0.5rem;
	margin-bottom: 1.5rem;
`;

const Check = styled(BSForm.Check)`
    display: inline;
`;

const CloseButton = styled.div`
	background-color: #424250;
	width: 2rem;
	height: 2rem;
	border-radius: 8px;
	display: flex;
	justify-content: center;
	align-items: center;
	color: white;
	position: absolute;
	top: 12px;
	right: 10px;
`;

const AccessLevelsBox = styled.div`
	border: 1px solid rgba(0, 0, 0, 0.33);
	border-radius: 12px;
	width: 100%;	
	position: relative;
	color: rgba(0, 0, 0, 0.3);
	margin-top: 2rem;
    padding-top: 1rem;
	height: 14rem;

	.label {
		border: 1px solid rgba(0, 0, 0, 0.33);
		border-radius: 8px;
		position: absolute;
		background-color: white;
		top: -0.9rem;
		right: 1rem;
		padding: 0.2rem 0.7rem;
		font-size: 10pt;
		z-index: 2;
	}

	.rows {
		overflow-y: scroll;				
		height: 100%;
	}
	.level-row {		
		width: 100%;
		display: flex;
		justify-content: end;
		align-items: center;
		padding: 0.6rem 1.5rem;
	}
`;

const GroupAccessType = {
	admin: { label: 'کشوری', value: 2 },
	union: { label: 'استانی', value: 1 },
}

const AddUserGroupModal = ({
	isOpen,
	onClose,
	onCreateSuccess,
	data,
	userType,
	...rest
}) => {
	const [currentData, setCurrentData] = useState();
	const [loading, setLoading] = useState(false);
	const [selectedGroupType, setSelectedGroupType] = useState();
	const [selectedPermissions, setSelectedPermissions] = useState([]);
	const [formikKey, setFormikKey] = useState(0);

	const isEditMode = useCallback(
		() => currentData && currentData != null,
		[currentData]
	);

	useEffect(() => {
		setCurrentData(data);
	}, [data]);

	useEffect(() => {
		if (currentData && currentData != null) {
			setSelectedGroupType({
				label: (currentData.user_type === GroupAccessType.admin.value) ? 'کشوری' : 'استانی',
				value: currentData.user_type
			});
			setSelectedPermissions(currentData.permissions);
		} else {
			setSelectedGroupType(null);
			setSelectedPermissions([]);
		}

		setFormikKey((prev) => prev + 1);
	}, [currentData]);

	const { data: permissions } = useQuery(
		["get-permissions"],
		() => getPermissions(`?user_type=${userType}`),
		{
			refetchOnWindowFocus: false,
		}
	);

	const userGroupMutation = useMutation(
		isEditMode() ? updateUserGroup : createUserGroup,
		{
			onSuccess: (res) => {
				if (res && res.data) {
					toast(`با موفقیت ${isEditMode() ? "ویرایش" : "ایجاد"} گردید`, {
						type: "success",
						position: "bottom-right",
					});
					onCreateSuccess?.();
					onClose();
				}
			},
			onError: (err) => {
				toast(getErrorMessage(err), {
					type: "error",
					position: "bottom-right",
				});
			},
			onSettled: () => {
				setLoading(false);
			},
		}
	);

	const getGroupTypeOptions = () => [GroupAccessType.admin, GroupAccessType.union];

	const onPermissionCheck = e => {
		const id = e.target.id;
		const checked = e.target.checked;

		const currentPermissions = [...selectedPermissions];
		if (checked) {
			currentPermissions.push(parseInt(id));
		} else {
			const index = currentPermissions.findIndex((item) => parseInt(item) === parseInt(id));
			currentPermissions.splice(index, 1);
		}

		setSelectedPermissions(currentPermissions);
	};

	const isChecked = permissionId => selectedPermissions
		?.findIndex((item) => `${item}` === `${permissionId}`) > -1;

	return (
		<ModalHolder show={isOpen} size={'xl'} centered animation={false} onHide={onClose} {...rest}>
			<Modal.Body className="px-5">
				<CloseButton onClick={onClose}>
					<MdClose color="#fff" />
				</CloseButton>
				<Header>
					<span>{isEditMode() ? "ویرایش" : "افزودن"} گروه کاربری</span>
				</Header>
				<Formik
					key={formikKey}
					initialValues={{
						name: data?.name,
						description: data?.description,
					}}
					validate={(values) => {
						const errors = {};
						if (isBlank(values.name)) {
							errors.name = "ضروری";
						}
						return errors;
					}}
					onSubmit={(values, { setSubmitting }) => {
						setLoading(true);
						userGroupMutation.mutate({
							id: data?.id,
							permissions: selectedPermissions.map((p) => parseInt(p)),
							...values,
						});
					}}
				>
					{({ submitForm }) => (
						<Form>
							<Fragment>
								<div className="w-100 d-flex justify-content-end">
									{/* <Col md={6}>
										<Field type="text" name="description">
											{({ field, meta: { touched, error } }) => (
												<CustomInput
													className={touched && error ? "invalid" : ""}
													error={!isBlank(error)}
													{...field}
													placeholder="توضیحات"
													radius="14"
												/>
											)}
										</Field>
										<ErrorMessage
											name="description"
											component={FormikFieldError}
										/>
									</Col>
									<Col md={3} className="px-2">
										<Field type="text" name="default_unit_id">
											{({ field, meta: { touched, error } }) => (
												<CustomSelect
													className={touched && error ? "invalid" : ""}
													error={!isBlank(error)}
													{...field}
													placeholder="نوع دسترسی"
													radius="14"
													options={getGroupTypeOptions()}
													onChange={setSelectedGroupType}
													value={selectedGroupType}
												/>
											)}
										</Field>
										<ErrorMessage
											name="default_unit_id"
											component={FormikFieldError}
										/>
									</Col> */}
									<Col md={3}>
										<Field type="text" name="name">
											{({ field, meta: { touched, error } }) => (
												<CustomInput
													className={touched && error ? "invalid" : ""}
													error={!isBlank(error)}
													{...field}
													placeholder="نام گروه کاربری"
													radius="14"
												/>
											)}
										</Field>
										<ErrorMessage name="name" component={FormikFieldError} />
									</Col>
								</div>
								<AccessLevelsBox>
									<div className="label">سطح دسترسی ها</div>
									<div className="rows">
										{permissions?.data?.map((permission, index) => (
											<div className="level-row" key={permission.id}>
												<label className='me-4' htmlFor={permission.id}>
													{permission.name}
												</label>
												<Check
													type="checkbox"
													id={permission.id}
													onChange={onPermissionCheck}
													checked={isChecked(permission.id)}
												/>
											</div>
										))}
									</div>
								</AccessLevelsBox>
								<div className="d-flex justify-content-center mt-4 mb-3 w-100">
									<CustomButton
										fontSize={16}
										onClick={submitForm}
										disabled={loading}
										className="w-25 d-flex align-items-center justify-content-center"
									>
										{loading ? (
											<Spinner animation="border" role="status" size="sm" />
										) : (
											<span>ثبت</span>
										)}
									</CustomButton>
									<CustomButton
										className="ms-3 w-25"
										color="#7300FF"
										fontSize={16}
										 onClick={onClose}
									>
										انصراف
									</CustomButton>
								</div>
							</Fragment>
						</Form>
					)}
				</Formik>
			</Modal.Body>
		</ModalHolder>
	);
};

export default AddUserGroupModal;
